/**
 * Regex-based IOC (Indicator of Compromise) extraction.
 *
 * Runs before the LLM entity extractor to deterministically extract
 * technical indicators: CVE IDs, IP addresses, hashes, domains, etc.
 *
 * These are 100% precision extractions — no LLM needed.
 * Extracted IOCs become entities with extraction_method: 'regex' in provenance.
 */

export interface ExtractedIOC {
  type: 'CVE' | 'IPv4' | 'IPv6' | 'MD5' | 'SHA1' | 'SHA256' | 'DOMAIN' | 'EMAIL' | 'CIDR';
  value: string;
  offset: number; // character position in source text
}

const IOC_PATTERNS: Record<ExtractedIOC['type'], RegExp> = {
  CVE: /CVE-\d{4}-\d{4,7}/gi,
  IPv4: /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\b/g,
  IPv6: /\b(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b/g,
  MD5: /\b[0-9a-fA-F]{32}\b/g,
  SHA1: /\b[0-9a-fA-F]{40}\b/g,
  SHA256: /\b[0-9a-fA-F]{64}\b/g,
  DOMAIN: /\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+(?:com|org|net|gov|mil|edu|io|co|uk|de|ru|cn|info|biz)\b/gi,
  EMAIL: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  CIDR: /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\/(?:3[0-2]|[12]?\d)\b/g,
};

/**
 * Extract all IOCs from text using regex patterns.
 * De-duplicates by type:value. CIDR patterns are checked before IPv4 to
 * avoid matching the IP portion of a CIDR range separately.
 */
export function extractIOCs(text: string): ExtractedIOC[] {
  const results: ExtractedIOC[] = [];
  const seen = new Set<string>();

  // Process CIDR before IPv4 so we can exclude CIDR IPs from plain IPv4 matches
  const orderedTypes: ExtractedIOC['type'][] = [
    'CVE', 'CIDR', 'IPv4', 'IPv6', 'SHA256', 'SHA1', 'MD5', 'DOMAIN', 'EMAIL',
  ];

  // Track CIDR IP portions to exclude from IPv4 matches
  const cidrIps = new Set<string>();

  for (const type of orderedTypes) {
    const pattern = IOC_PATTERNS[type];
    const regex = new RegExp(pattern.source, pattern.flags);
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const value = match[0];

      // Skip IPv4 matches that are part of a CIDR range
      if (type === 'IPv4' && cidrIps.has(value)) continue;

      // Track CIDR IP portion
      if (type === 'CIDR') {
        cidrIps.add(value.split('/')[0]);
      }

      // Skip SHA256 values that were already matched as shorter hashes
      // (SHA256 is 64 chars, SHA1 is 40, MD5 is 32 — longer matches take priority)
      const key = `${type}:${value}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ type, value, offset: match.index });
      }
    }
  }

  return results;
}
